import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Calculator, TrendingUp, Download, Share2, BookOpen, Info } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const DividendesSimulator = () => {
  const [inputs, setInputs] = useState({
    resultatNet: '50000',
    statut: 'sasu',
    montantDividendes: '30000',
    capitalSocial: '1000'
  });
  const [results, setResults] = useState({});

  const PASS_2026 = 46368;

  const calculate = () => {
    const resultatNet = parseFloat(inputs.resultatNet) || 0;
    const dividendes = Math.min(parseFloat(inputs.montantDividendes) || 0, resultatNet);
    const capital = parseFloat(inputs.capitalSocial) || 1000;

    if (inputs.statut === 'sasu') {
      const flatTax = dividendes * 0.30;
      const dividendesNets = dividendes - flatTax;
      const ps = dividendes * 0.172;
      const ir = dividendes * 0.128;
      setResults({
        dividendes, flatTax: Math.round(flatTax), dividendesNets: Math.round(dividendesNets),
        ps: Math.round(ps), ir: Math.round(ir),
        tauxEffectif: dividendes > 0 ? (flatTax / dividendes * 100).toFixed(1) : '0',
        mode: 'Flat Tax (PFU 30%)',
        reste: Math.round(resultatNet - dividendes),
        netMensuel: Math.round(dividendesNets / 12)
      });
    } else {
      // EURL: dividendes > 10% du capital soumis à cotisations sociales
      const seuil10pct = capital * 0.10;
      const partFlatTax = Math.min(dividendes, seuil10pct);
      const partCotisations = Math.max(dividendes - seuil10pct, 0);
      const flatTax = partFlatTax * 0.30;
      const cotisations = partCotisations * 0.45;
      const totalPrelevements = flatTax + cotisations;
      const dividendesNets = dividendes - totalPrelevements;

      setResults({
        dividendes, flatTax: Math.round(flatTax), cotisations: Math.round(cotisations),
        totalPrelevements: Math.round(totalPrelevements),
        dividendesNets: Math.round(dividendesNets),
        tauxEffectif: dividendes > 0 ? (totalPrelevements / dividendes * 100).toFixed(1) : '0',
        mode: 'Flat tax + Cotisations TNS (>10% capital)',
        seuil10pct: Math.round(seuil10pct),
        partFlatTax: Math.round(partFlatTax),
        partCotisations: Math.round(partCotisations),
        reste: Math.round(resultatNet - dividendes),
        netMensuel: Math.round(dividendesNets / 12)
      });
    }
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: 'Simulateur Dividendes - Bozzam', url }); }
    else { navigator.clipboard.writeText(url); alert('Lien copié !'); }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255); doc.setFontSize(20);
    doc.text('Bozzam - Dividendes SASU/EURL', 15, 25);
    doc.setFontSize(10); doc.text('blog.bozzam.com', 15, 35);
    doc.setTextColor(0); let y = 55;
    doc.setFontSize(14); doc.text('Paramètres', 15, y); y += 10;
    doc.setFontSize(11);
    doc.text(`Statut : ${inputs.statut.toUpperCase()}`, 15, y); y += 7;
    doc.text(`Résultat net : ${parseInt(inputs.resultatNet).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Dividendes : ${results.dividendes?.toLocaleString()} EUR`, 15, y); y += 15;
    doc.setFontSize(14); doc.text('Résultats', 15, y); y += 10;
    doc.setFontSize(11);
    doc.text(`Mode : ${results.mode}`, 15, y); y += 7;
    doc.text(`Dividendes nets : ${results.dividendesNets?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Taux effectif : ${results.tauxEffectif}%`, 15, y); y += 7;
    doc.text(`Revenu mensuel : ${results.netMensuel?.toLocaleString()} EUR/mois`, 15, y); y += 15;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Sources : CGI art. 200 A (flat tax 30%), CSS art. L131-6 (EURL TNS)', 15, y);
    doc.save('simulation-dividendes-bozzam.pdf');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" /> Paramètres dividendes
            </CardTitle>
            <CardDescription>Optimisez vos dividendes selon votre statut</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Statut</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Button data-testid="statut-sasu-btn"
                  variant={inputs.statut === 'sasu' ? 'default' : 'outline'}
                  className={inputs.statut === 'sasu' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => handleInputChange('statut', 'sasu')}>SASU</Button>
                <Button data-testid="statut-eurl-btn"
                  variant={inputs.statut === 'eurl' ? 'default' : 'outline'}
                  className={inputs.statut === 'eurl' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => handleInputChange('statut', 'eurl')}>EURL</Button>
              </div>
            </div>
            <div>
              <Label>Résultat net de la société (€)</Label>
              <Input data-testid="resultat-input" type="text" inputMode="numeric" value={inputs.resultatNet}
                onChange={(e) => handleInputChange('resultatNet', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="50000" className="mt-1" />
            </div>
            <div>
              <Label>Montant dividendes à distribuer (€)</Label>
              <Input data-testid="dividendes-input" type="text" inputMode="numeric" value={inputs.montantDividendes}
                onChange={(e) => handleInputChange('montantDividendes', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="30000" className="mt-1" />
            </div>
            {inputs.statut === 'eurl' && (
              <div>
                <Label>Capital social (€)</Label>
                <Input data-testid="capital-input" type="text" inputMode="numeric" value={inputs.capitalSocial}
                  onChange={(e) => handleInputChange('capitalSocial', e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="1000" className="mt-1" />
              </div>
            )}
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>SASU : Flat tax 30% (12.8% IR + 17.2% PS) - CGI art. 200 A</li>
                    <li>EURL : Dividendes {'>'} 10% capital = cotisations TNS ~45%</li>
                    <li>CSS art. L131-6 (assiette cotisations gérant EURL)</li>
                    <li>PASS 2026 : 46 368€</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" /> Résultats
            </CardTitle>
            <Badge className="bg-blue-100 text-blue-800">{inputs.statut.toUpperCase()}</Badge>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xs text-blue-600 mb-1">Dividendes bruts</p>
              <p className="text-2xl font-bold text-blue-900" data-testid="result-bruts">{results.dividendes?.toLocaleString()}€</p>
            </div>

            {inputs.statut === 'sasu' ? (
              <div className="space-y-2">
                <div className="flex justify-between p-3 bg-red-50 rounded">
                  <span className="text-sm text-red-700">Prélèvements sociaux (17.2%)</span>
                  <span className="text-sm font-bold text-red-900">-{results.ps?.toLocaleString()}€</span>
                </div>
                <div className="flex justify-between p-3 bg-red-50 rounded">
                  <span className="text-sm text-red-700">IR forfaitaire (12.8%)</span>
                  <span className="text-sm font-bold text-red-900">-{results.ir?.toLocaleString()}€</span>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="p-3 bg-amber-50 rounded border border-amber-200">
                  <p className="text-xs text-amber-700">Seuil 10% capital : {results.seuil10pct?.toLocaleString()}€</p>
                </div>
                <div className="flex justify-between p-3 bg-red-50 rounded">
                  <span className="text-sm text-red-700">Flat tax ({results.partFlatTax?.toLocaleString()}€)</span>
                  <span className="text-sm font-bold text-red-900">-{results.flatTax?.toLocaleString()}€</span>
                </div>
                <div className="flex justify-between p-3 bg-red-50 rounded">
                  <span className="text-sm text-red-700">Cotisations TNS ({results.partCotisations?.toLocaleString()}€)</span>
                  <span className="text-sm font-bold text-red-900">-{results.cotisations?.toLocaleString()}€</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200 text-center">
                <p className="text-sm text-green-600 mb-1">Dividendes nets</p>
                <p className="text-2xl font-black text-green-900" data-testid="result-nets">{results.dividendesNets?.toLocaleString()}€</p>
                <p className="text-xs text-green-700">{results.netMensuel?.toLocaleString()}€/mois</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <p className="text-sm text-purple-600 mb-1">Reste en société</p>
                <p className="text-2xl font-bold text-purple-900">{results.reste?.toLocaleString()}€</p>
              </div>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <span className="font-semibold">Taux effectif d'imposition : {results.tauxEffectif}%</span>
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Partager</Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-2" /> PDF</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>Bozzam App</Button>
            </div>
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              <p>Sources : CGI art. 200 A, CSS art. L131-6, URSSAF 2026</p>
              <p>bofip.impots.gouv.fr | urssaf.fr | legifrance.gouv.fr</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DividendesSimulator;
